import { Component, Prop, h } from '@stencil/core';

@Component({
  tag: 'a1-button',
  styleUrl: 'a1-button.scss',
  scoped: true,
})
export class A1Button {
  /**
   * Button kind: with or without background shape.
   */
  @Prop({ reflect: true }) kind: 'contained' | 'plain' = 'contained';

  /**
   * Button size.
   */
  @Prop({ reflect: true }) size: 'm' | 's' = 'm';

  /**
   * Button type, which indicates hierarchy or type of action.
   */
  @Prop({ reflect: true }) type: 'primary' | 'secondary' | 'tertiary' = 'primary';

  /**
   * Text label for button.
   */
  @Prop({ reflect: true }) text: string = '';

  /**
   * Toggle for enabling / disabling button.
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Toggle for regular / inverse button appearance.
   */
  @Prop({ reflect: true }) inverse: boolean = false;

  /**
   * Toggle for regular / critical button appearance.
   * Only affects the primary button variant.
   */
  @Prop({ reflect: true }) critical: boolean = false;

  /**
   * Toggle for expanding button to the width of its container.
   */
  @Prop({ reflect: true }) fullWidth: boolean = false;

  /**
   * Button icon position relative to button text.
   */
  @Prop({ reflect: true }) iconPosition: 'before' | 'after' | 'none' = 'none';

  /**
   * Toggle for select indicator type.
   */
  @Prop({ reflect: true }) indicatorType: 'indicator' | 'indicator-count' | 'none' = 'none';

  /**
   * Text label for indicator counter value.
   */
  @Prop({ reflect: true }) indicatorCounter: number = 0;

  private getClassMap() {
    const finalPosition = 'icon-' + this.iconPosition;
    return {
      [this.kind]: true,
      [this.size]: true,
      [this.type]: true,
      'inverse': !this.critical && this.inverse,
      'critical': this.type === 'primary' && this.critical,
      'full-width': this.kind !== 'plain' && this.fullWidth,
      ...(this.iconPosition !== 'none' ? { [finalPosition]: finalPosition } : {}),
      'icon-only': this.text === '',
      [this.indicatorType]: this.indicatorType !== 'none' && this.type === 'tertiary',
    };
  }

  private classNames(classes: { [key: string]: string | boolean | number }): string {
    return Object.keys(classes)
      .filter(key => !!classes[key])
      .join(' ');
  }

  render() {
    const classes = this.getClassMap();
    const classString = this.classNames(classes);

    const iconTest = (
      <span class="btn-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M20,16.228C19.561,16.228 19.2,16.589 19.2,17.028L19.2,19.228L4.8,19.228L4.8,17.028C4.8,16.589 4.439,16.228 4,16.228C3.561,16.228 3.2,16.589 3.2,17.028L3.2,20.028C3.2,20.467 3.561,20.828 4,20.828L20,20.828C20.439,20.828 20.8,20.467 20.8,20.028L20.8,17.028C20.8,16.589 20.439,16.228 20,16.228Z"></path>
          <path d="M11.292,16.733C11.48,16.921 11.734,17.027 12,17.027C12.266,17.027 12.52,16.921 12.708,16.733L16.708,12.733C16.888,12.547 16.989,12.297 16.989,12.038C16.989,11.49 16.537,11.038 15.989,11.038C15.73,11.038 15.48,11.139 15.294,11.319L13,13.613L13,4.027C13,3.478 12.549,3.027 12,3.027C11.451,3.027 11,3.478 11,4.027L11,13.613L8.707,11.32C8.521,11.14 8.271,11.039 8.012,11.039C7.464,11.039 7.012,11.491 7.012,12.039C7.012,12.298 7.113,12.548 7.293,12.734L11.292,16.733Z"></path>
        </svg>
      </span>
    );

    return (
      <button class={classString} disabled={this.disabled} tabindex={this.disabled ? '-1' : '0'}>
        {(this.text === '' || this.iconPosition !== 'none') && iconTest}
        {this.type === 'tertiary' && this.indicatorType === 'indicator' && (
          <div class="btn-indicator">
            <a1-indicator></a1-indicator>
          </div>
        )}
        {this.type === 'tertiary' && this.indicatorType === 'indicator-count' && (
          <div class="btn-count-indicator">
            <a1-indicator count={1234} maxDigits={3}></a1-indicator>
          </div>
        )}
        {this.text}
      </button>
    );
  }
}
